/**
 * Roster cache (village devices) and site cache (store devices).
 *
 * Shapes follow the PROPOSED GET /devices/me/roster in docs/f4-f5/sections/F4.1.md (task F4.1-f).
 * Unknown extra fields are kept (loose objects) so a backend that sends more does not break old
 * phones. The roster carries NO money figures for farmers and NO full phone numbers — the
 * only rupee figure is the published price per kg on a rate card.
 *
 * UNITS on the wire (alignment with uc-core, F4.1-s "numeric columns as strings"):
 *  - kg figures (season kg, ceilings, sack tare, config weights) arrive as decimal kg (number or
 *    string) and are converted ONCE here to integer grams (`*_g`) with string arithmetic;
 *  - `base_price_per_kg` arrives as decimal rupees ("62.00") and becomes integer paise
 *    (`price_paise_per_kg`);
 *  - moisture percentages arrive as decimals and become integer tenths of a percent (`*_tenths`).
 * A sender may instead send the integer field directly (`tare_g`, `price_paise_per_kg`, …).
 */
import { z } from 'zod'
import { parseKgToGrams } from '@/lib/weight'
import { parseRupeesToPaise } from '@/lib/money'
import { getDb, ROSTER_TABLES, type CacheRow, type RosterTable, type SupplyChainDB } from './db'
import { getMeta, setMeta } from './meta'
import { importVaultKey, maybeSeal, maybeUnseal } from './vault'

const decimal = z.union([z.number(), z.string()])

/** Decimal kg (number or string) → integer grams; null when absent or unparsable. */
export function kgToGrams(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null
  return parseKgToGrams(typeof v === 'number' ? v.toFixed(3) : v)
}

/** Decimal percent ("12.4", 9) → integer tenths (124, 90); null when absent or unparsable. */
export function pctToTenths(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null
  const s = typeof v === 'number' ? v.toFixed(1) : v.trim()
  const m = /^(\d+)(?:\.(\d))?\d*$/.exec(s)
  if (!m) return null
  return Number(m[1]) * 10 + Number(m[2] ?? '0')
}

export const farmerSchema = z.looseObject({
  user_id: z.string(),
  /** The number on the printed village roster (users.farmer_code), e.g. "017" or "BELU-017". */
  roster_no: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  father_name: z.string().nullish(),
  /** Last 4 digits of users.contact_no; null = no phone on file (photo check only). */
  phone_last4: z.string().nullish(),
  photo_url: z.string().nullish(),
  cultivations: z
    .array(
      z.looseObject({
        id: z.string(),
        crop_id: z.string(),
        crop_variety_id: z.string().nullish(),
        plot_label: z.string().nullish(),
        area: decimal.nullish(),
        area_unit: z.string().nullish(),
        photo_url: z.string().nullish(),
        /** Farm, variety and sowing date shown on the gate's cultivation card. */
        farm_name: z.string().nullish(),
        variety_name: z.string().nullish(),
        start_date: z.string().nullish(),
      }),
    )
    .default([]),
  bank: z
    .looseObject({
      verified: z.boolean(),
      account_last4: z.string().nullish(),
      /** An account checked (₹1) and waiting for the office's approval. */
      pending_last4: z.string().nullish(),
    })
    .nullish(),
  /** Season kg so far and the ceiling; the phone shows only a green/amber light. */
  season: z
    .array(
      z.looseObject({
        crop_id: z.string(),
        kg_to_date: decimal,
        ceiling_kg: decimal.nullish(),
      }),
    )
    .default([]),
})
export type RosterFarmer = z.infer<typeof farmerSchema>
export type RosterCultivation = RosterFarmer['cultivations'][number] & { farmer_id: string }

export const teamMemberSchema = z.looseObject({
  user_id: z.string(),
  name: z.string(),
  roles: z.array(z.string()).default([]),
  /** Encoded PIN check, see auth/pin/PinVerifier.ts. Null when the person has not set a PIN. */
  pin_hash: z.string().nullish(),
  /**
   * Set when this operator has an OPEN SESSION_UNRECONCILED exception: the phone refuses to open
   * her next day (F4.1). Cleared in the next roster after a supervisor resolves it.
   */
  blocked: z
    .looseObject({ exception_id: z.string().nullish(), session_code: z.string().nullish() })
    .nullish(),
  /** Multi-village phones (V1): the villages this person works in, offered after the PIN. */
  villages: z.array(z.looseObject({ id: z.string(), name: z.string() })).optional(),
})
export type TeamMember = z.infer<typeof teamMemberSchema>

export const ROLL_KINDS = ['BAG', 'SEAL'] as const
export type RollKind = (typeof ROLL_KINDS)[number]

/**
 * A tag roll or seal strip of this village (qr_batches). The roster carries every roll of the
 * village that is not returned — assigned ones with `issued_to_id`, unassigned spares with null —
 * so a supervisor can assign one on the spot (F4.1).
 */
export const rollSchema = z.looseObject({
  batch_id: z.string(),
  kind: z.enum(ROLL_KINDS).or(z.string()),
  start_sequence: z.number(),
  end_sequence: z.number(),
  /** Printed text before the number, e.g. "RJ-BELU-" for RJ-BELU-017. */
  payload_prefix: z.string(),
  /** Digits of the number part (zero padded); null = no padding. */
  sequence_digits: z.number().nullish(),
  /** Every code of the roll in sequence order (server list; preferred over prefix + number). */
  payloads: z.array(z.string()).optional(),
  spare: z.boolean().default(false),
  issued_to_id: z.string().nullish(),
  /**
   * V1: the office issued this roll to the village (qr_batches.issued_at); its codes are live at
   * once for every operator of the village, with no confirm scan. Absent from an older roster.
   */
  issued: z.boolean().nullish(),
  /** Codes of this roll the server already holds as BOUND / VOID (e.g. bound on another phone). */
  bound_payloads: z.array(z.string()).default([]),
  voided_payloads: z.array(z.string()).default([]),
})
export type RosterRoll = z.infer<typeof rollSchema>

export const rateCardSchema = z
  .looseObject({
    id: z.string(),
    crop_id: z.string(),
    stream: z.enum(['MAIN', 'CONV']).or(z.string()),
    version: z.union([z.number(), z.string()]).nullish(),
    status: z.string().default('PUBLISHED'),
    /** IST dates "2026-10-01"; null = open ended. */
    effective_from: z.string().nullish(),
    effective_to: z.string().nullish(),
    /** Decimal rupees per kg as stored in rate_cards ("62.00"). */
    base_price_per_kg: decimal.nullish(),
    /** Integer paise per kg; wins over base_price_per_kg when sent. */
    price_paise_per_kg: z.number().int().nullish(),
    /** Empty (null, [] or {}) for every card the phone may use. */
    deduction_rules: z.unknown().nullish(),
  })
  .transform((c) => ({
    ...c,
    price_paise_per_kg:
      c.price_paise_per_kg ??
      (c.base_price_per_kg === null || c.base_price_per_kg === undefined
        ? null
        : parseRupeesToPaise(
            typeof c.base_price_per_kg === 'number'
              ? c.base_price_per_kg.toFixed(2)
              : c.base_price_per_kg,
          )),
  }))
export type RosterRateCard = z.infer<typeof rateCardSchema>

export const sackTypeSchema = z
  .looseObject({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    tare_kg: decimal.nullish(),
    tare_g: z.number().int().nullish(),
    active: z.boolean().default(true),
  })
  .transform((s) => ({ ...s, tare_g: s.tare_g ?? kgToGrams(s.tare_kg) ?? 0 }))
export type RosterSackType = z.infer<typeof sackTypeSchema>

/** Registered gate scales and moisture meters of this village (equipment, F4.4-e). */
export const equipmentSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  category: z.enum(['SCALE', 'MOISTURE_METER']).or(z.string()),
  name: z.string().nullish(),
  serial_no: z.string().nullish(),
  /** Legal Metrology stamp due date (IST date). Red when passed. */
  stamp_due_at: z.string().nullish(),
  calibrated_at: z.string().nullish(),
  calibration_due_at: z.string().nullish(),
})
export type RosterEquipment = z.infer<typeof equipmentSchema>

/** Numbered carbon receipt books issued in this village (receipt_books, F4-B3.a). */
export const receiptBookSchema = z.looseObject({
  id: z.string(),
  book_no: z.union([z.string(), z.number()]).transform(String),
  first_slip_no: z.number().int(),
  last_slip_no: z.number().int(),
  issued_to_id: z.string().nullish(),
  /** Slip numbers the server already holds as USED or SPOILED. */
  used_slip_nos: z.array(z.number().int()).default([]),
})
export type RosterReceiptBook = z.infer<typeof receiptBookSchema>

const reasonSchema = z
  .union([
    z.string(),
    z.looseObject({ code: z.string(), hi: z.string().nullish(), en: z.string().nullish() }),
  ])
  .transform((r) => (typeof r === 'string' ? { code: r, hi: null, en: null } : r))
export type RosterReason = z.infer<typeof reasonSchema>

export const cropConfigSchema = z
  .looseObject({
    crop_id: z.string(),
    name_hi: z.string().nullish(),
    name_en: z.string().nullish(),
    /** Groundnut: a reading is required; skipping needs a supervisor (F4.4). */
    moisture_required: z.boolean().default(false),
    moisture_line_pct: decimal.nullish(),
    /** Minimum retained-sample weight in grams (F4.7). */
    sample_min_g: z.number().int().default(500),
  })
  .transform((c) => ({ ...c, moisture_line_tenths: pctToTenths(c.moisture_line_pct) }))
export type RosterCropConfig = z.infer<typeof cropConfigSchema>

const DEFAULT_RED_LINE = [
  'MOULD',
  'SPROUTING',
  'INSECT_DAMAGE',
  'FOREIGN_MATTER',
  'CHEMICAL_SACK',
  'OTHER',
]
const DEFAULT_DISPUTE = {
  WEIGHT: ['TYPED_DIFFERS', 'SCALE_NOT_SETTLED', 'SCALE_WRONG', 'OTHER'],
  GRADE: ['DEFECT_NOT_THERE', 'WRONG_LINE', 'OTHER'],
  MOISTURE: ['READING_TOO_HIGH', 'OTHER'],
}

/** Roster `config` (F4.1-f) with October defaults so an older roster still works. */
export const gateConfigSchema = z
  .looseObject({
    /**
     * Build scope from the server (uc-core SCOPE): V1 = Seva Mandir V1, the cut-down field flow
     * agreed on 6 Oct 2026; FULL = the whole spec. The phone always follows the server.
     */
    scope: z.enum(['V1', 'FULL']).or(z.string()).default('V1'),
    /** F5.1: the store-cache site a village phone loads vehicles for (store flows). */
    collection_point_id: z.string().nullish(),
    red_line_reasons: z
      .array(reasonSchema)
      .default(DEFAULT_RED_LINE.map((code) => ({ code, hi: null, en: null }))),
    crops: z.array(cropConfigSchema).default([]),
    moisture_spread_max: decimal.default('1.5'),
    moisture_min_pct: decimal.default('3.0'),
    moisture_max_pct: decimal.default('40.0'),
    weight_min_kg: decimal.default('5'),
    weight_max_kg: decimal.default('60'),
    /**
     * Back up open farmers to the server (POST /devices/me/delivery-drafts). Off unless the server
     * says so: an older server never gets the calls.
     */
    draft_backup: z.boolean().default(false),
    test_weight_kg: decimal.default('20.000'),
    test_weight_tolerance_kg: decimal.default('0.100'),
    /** Bags brought above this ask to confirm (F4.3). */
    bags_confirm_above: z.number().int().default(40),
    /** Sample weight above this asks to confirm (F4.7). */
    sample_confirm_above_g: z.number().int().default(3000),
    divert_reasons: z.array(reasonSchema).default([{ code: 'LOOKS_WET', hi: null, en: null }]),
    dispute_reasons: z
      .looseObject({
        WEIGHT: z
          .array(reasonSchema)
          .default(DEFAULT_DISPUTE.WEIGHT.map((code) => ({ code, hi: null, en: null }))),
        GRADE: z
          .array(reasonSchema)
          .default(DEFAULT_DISPUTE.GRADE.map((code) => ({ code, hi: null, en: null }))),
        MOISTURE: z
          .array(reasonSchema)
          .default(DEFAULT_DISPUTE.MOISTURE.map((code) => ({ code, hi: null, en: null }))),
      })
      .default({
        WEIGHT: DEFAULT_DISPUTE.WEIGHT.map((code) => ({ code, hi: null, en: null })),
        GRADE: DEFAULT_DISPUTE.GRADE.map((code) => ({ code, hi: null, en: null })),
        MOISTURE: DEFAULT_DISPUTE.MOISTURE.map((code) => ({ code, hi: null, en: null })),
      }),
  })
  .transform((c) => ({
    ...c,
    /** Seva Mandir V1 is on (anything but FULL). */
    v1: c.scope !== 'FULL',
    moisture_spread_max_tenths: pctToTenths(c.moisture_spread_max) ?? 15,
    moisture_min_tenths: pctToTenths(c.moisture_min_pct) ?? 30,
    moisture_max_tenths: pctToTenths(c.moisture_max_pct) ?? 400,
    weight_min_g: kgToGrams(c.weight_min_kg) ?? 5000,
    weight_max_g: kgToGrams(c.weight_max_kg) ?? 60000,
    test_weight_g: kgToGrams(c.test_weight_kg) ?? 20000,
    test_weight_tolerance_g: kgToGrams(c.test_weight_tolerance_kg) ?? 100,
  }))
export type GateConfig = z.infer<typeof gateConfigSchema>

export const rosterSnapshotSchema = z.looseObject({
  roster_version: z.union([z.string(), z.number()]).transform(String),
  /** The village this roster is for (the phone's village; it changes when she switches). */
  village: z
    .looseObject({ group_id: z.string(), name: z.string().nullish(), code: z.string().nullish() })
    .nullish(),
  generated_at: z.string(),
  farmers: z.array(farmerSchema).default([]),
  rate_cards: z.array(rateCardSchema).default([]),
  sack_types: z.array(sackTypeSchema).default([]),
  rolls: z.array(rollSchema).default([]),
  team: z.array(teamMemberSchema).default([]),
  equipment: z.array(equipmentSchema).default([]),
  receipt_books: z.array(receiptBookSchema).default([]),
  config: gateConfigSchema.default(gateConfigSchema.parse({})),
  /** Per-device AES-256 key (base64) for at-rest encryption, when the server issues one. */
  vault_key: z.string().nullish(),
})
export type RosterSnapshot = z.infer<typeof rosterSnapshotSchema>
/** The village roster as the phone holds it (GET /devices/me/roster, F4.1-f), units normalised. */
export type Roster = Omit<RosterSnapshot, 'vault_key'>
/** What uc-core sends (before unit normalisation). Use this type when building the endpoint. */
export type RosterWire = z.input<typeof rosterSnapshotSchema>

export const locationSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  name: z.string().nullish(),
  kind: z.string().nullish(),
  parent_id: z.string().nullish(),
  qr_payload: z.string().nullish(),
})
export type CachedLocation = z.infer<typeof locationSchema>

export const siteCacheSchema = z.looseObject({
  site_id: z.string(),
  generated_at: z.string(),
  locations: z.array(locationSchema).default([]),
  team: z.array(teamMemberSchema).default([]),
  config: z.record(z.string(), z.unknown()).default({}),
})
export type SiteCache = z.infer<typeof siteCacheSchema>

// ---------------------------------------------------------------------------------------------

export interface VaultKeys {
  key: CryptoKey | null
  id: string | null
  prev: CryptoKey | null
  prevId: string | null
}

let keyCache: VaultKeys | null = null

async function vaultKeys(db: SupplyChainDB): Promise<VaultKeys> {
  if (!keyCache) {
    const [key, id, prev, prevId] = await Promise.all([
      getMeta('vault_key', db),
      getMeta('vault_key_id', db),
      getMeta('vault_key_prev', db),
      getMeta('vault_key_prev_id', db),
    ])
    keyCache = { key: key ?? null, id: id ?? null, prev: prev ?? null, prevId: prevId ?? null }
  }
  return keyCache
}

async function vaultKey(db: SupplyChainDB): Promise<CryptoKey | null> {
  return (await vaultKeys(db)).key
}

/** The device's at-rest key, or null when the server issued none (store cache uses it too). */
export function getVaultKey(db: SupplyChainDB = getDb()): Promise<CryptoKey | null> {
  return vaultKey(db)
}

/** The current key with its id, and the one before it (outbox and drafts, see sealed.ts). */
export function getVaultKeys(db: SupplyChainDB = getDb()): Promise<VaultKeys> {
  return vaultKeys(db)
}

/** Short fingerprint of the key as the server sent it (SHA-256, hex). Never the key itself. */
async function keyId(rawBase64: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(rawBase64))
  return [...new Uint8Array(digest)]
    .slice(0, 16)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** Forget the in-memory key (after wipe or in tests). */
export function resetVaultKeyCache(): void {
  keyCache = null
}

async function rows<T>(
  key: CryptoKey | null,
  items: T[],
  id: (t: T) => string,
  ref?: (t: T) => string,
): Promise<CacheRow<T>[]> {
  return Promise.all(
    items.map(async (item) => ({
      id: id(item),
      ...(ref ? { ref: ref(item) } : {}),
      data: await maybeSeal(key, item),
    })),
  )
}

/**
 * Replace the whole roster atomically. A half-written roster is never visible.
 *
 * Resolves `keyChanged: true` when the server sent a key different from the one the phone holds
 * (first key, or rotated): the outbox and drafts then need `resealLocalData` (sealed.ts). The
 * old key is kept as `vault_key_prev` so rows sealed with it stay readable meanwhile.
 */
export async function saveRoster(
  snapshot: RosterSnapshot,
  db: SupplyChainDB = getDb(),
): Promise<{ keyChanged: boolean }> {
  const before = await vaultKeys(db)
  let key = before.key
  let next: VaultKeys | null = null
  if (snapshot.vault_key) {
    const id = await keyId(snapshot.vault_key)
    if (id !== before.id || !before.key) {
      key = await importVaultKey(snapshot.vault_key)
      next = before.key
        ? { key, id, prev: before.key, prevId: before.id }
        : { key, id, prev: before.prev, prevId: before.prevId }
    }
  }
  const { vault_key: _drop, ...plain } = snapshot

  // Encrypt outside the transaction (WebCrypto promises would end an IndexedDB transaction).
  const fields = plain.farmers.flatMap((f) =>
    f.cultivations.map((c) => ({ ...c, farmer_id: f.user_id })),
  )
  const prepared = {
    farmers: await rows(key, plain.farmers, (f) => f.user_id),
    fields: await rows(
      key,
      fields,
      (c) => c.id,
      (c) => c.farmer_id,
    ),
    rate_cards: await rows(key, plain.rate_cards, (r) => r.id),
    sack_types: await rows(key, plain.sack_types, (s) => s.id),
    rolls: await rows(key, plain.rolls, (r) => r.batch_id),
    team: await rows(key, plain.team, (t) => t.user_id),
    config: await rows(
      key,
      [
        { id: 'roster', ...plain.config },
        // Lists without a table of their own ride in the config table (no schema change).
        { id: 'equipment', items: plain.equipment },
        { id: 'receipt_books', items: plain.receipt_books },
      ],
      (c) => c.id,
    ),
  } satisfies Partial<Record<RosterTable, CacheRow[]>>

  await db.transaction(
    'rw',
    [db.farmers, db.fields, db.rate_cards, db.sack_types, db.rolls, db.team, db.config, db.meta],
    async () => {
      for (const [table, data] of Object.entries(prepared) as [
        keyof typeof prepared,
        CacheRow[],
      ][]) {
        await db[table].clear()
        await db[table].bulkPut(data)
      }
      if (next) {
        await setMeta('vault_key', next.key, db)
        await setMeta('vault_key_id', next.id, db)
        await setMeta('vault_key_prev', next.prev, db)
        await setMeta('vault_key_prev_id', next.prevId, db)
      }
      await setMeta('roster_version', plain.roster_version, db)
      await setMeta('roster_downloaded_at', Date.now(), db)
    },
  )
  if (next) keyCache = next
  return { keyChanged: next !== null }
}

export async function saveSiteCache(cache: SiteCache, db: SupplyChainDB = getDb()): Promise<void> {
  const key = await vaultKey(db)
  const locations = await rows(
    key,
    cache.locations,
    (l) => l.id,
    (l) => l.parent_id ?? '',
  )
  const team = await rows(key, cache.team, (t) => t.user_id)
  const config = await rows(key, [{ id: 'site', ...cache.config }], (c) => c.id)
  await db.transaction('rw', [db.locations, db.team, db.config, db.meta], async () => {
    await db.locations.clear()
    await db.locations.bulkPut(locations)
    await db.team.clear()
    await db.team.bulkPut(team)
    await db.config.put(config[0]!)
    await setMeta('roster_downloaded_at', Date.now(), db)
  })
}

async function readAll<T>(table: RosterTable, db: SupplyChainDB): Promise<T[]> {
  const key = await vaultKey(db)
  const all = await db[table].toArray()
  return Promise.all(all.map((r) => maybeUnseal<T>(key, r.data as T)))
}

async function readOne<T>(
  table: RosterTable,
  id: string,
  db: SupplyChainDB,
): Promise<T | undefined> {
  const row = await db[table].get(id)
  if (!row) return undefined
  return maybeUnseal<T>(await vaultKey(db), row.data as T)
}

/** Read API for feature code. All reads decrypt transparently. */
export const roster = {
  farmers: (db = getDb()) => readAll<RosterFarmer>('farmers', db),
  farmer: (id: string, db = getDb()) => readOne<RosterFarmer>('farmers', id, db),
  async fieldsOf(farmerId: string, db = getDb()): Promise<RosterCultivation[]> {
    const key = await vaultKey(db)
    const list = await db.fields.where('ref').equals(farmerId).toArray()
    return Promise.all(
      list.map((r) => maybeUnseal<RosterCultivation>(key, r.data as RosterCultivation)),
    )
  },
  rateCards: (db = getDb()) => readAll<RosterRateCard>('rate_cards', db),
  sackTypes: (db = getDb()) => readAll<RosterSackType>('sack_types', db),
  rolls: (db = getDb()) => readAll<RosterRoll>('rolls', db),
  team: (db = getDb()) => readAll<TeamMember>('team', db),
  teamMember: (userId: string, db = getDb()) => readOne<TeamMember>('team', userId, db),
  locations: (db = getDb()) => readAll<CachedLocation>('locations', db),
  async config<T = Record<string, unknown>>(
    name: 'roster' | 'site' = 'roster',
    db = getDb(),
  ): Promise<T | undefined> {
    return readOne<T>('config', name, db)
  },
  /** Typed village config (F4.1-f) with defaults filled in. */
  async gateConfig(db = getDb()): Promise<GateConfig> {
    const raw = await readOne<Record<string, unknown>>('config', 'roster', db)
    const parsed = gateConfigSchema.safeParse(raw ?? {})
    return parsed.success ? parsed.data : gateConfigSchema.parse({})
  },
  async equipment(db = getDb()): Promise<RosterEquipment[]> {
    return (await readOne<{ items: RosterEquipment[] }>('config', 'equipment', db))?.items ?? []
  },
  async receiptBooks(db = getDb()): Promise<RosterReceiptBook[]> {
    return (
      (await readOne<{ items: RosterReceiptBook[] }>('config', 'receipt_books', db))?.items ?? []
    )
  },
}

/** Case-insensitive search by name, father's name, roster number or phone last 4. */
export function searchFarmers(list: RosterFarmer[], q: string): RosterFarmer[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return list
  return list.filter(
    (f) =>
      f.name.toLowerCase().includes(needle) ||
      (f.father_name ?? '').toLowerCase().includes(needle) ||
      f.roster_no.toLowerCase() === needle ||
      (f.phone_last4 ?? '') === needle,
  )
}

/**
 * Wipe roster, PIN checks and the vault key (device revoked, R10). The outbox is kept.
 * `keepKey`: the outbox still holds sealed commands to send, so the key stays until they are
 * out (wipeDevice); without it they could never be opened again.
 */
export async function wipeRoster(
  db: SupplyChainDB = getDb(),
  opts: { keepKey?: boolean } = {},
): Promise<void> {
  await db.transaction('rw', [...ROSTER_TABLES.map((t) => db[t]), db.meta], async () => {
    for (const t of ROSTER_TABLES) await db[t].clear()
    if (!opts.keepKey) {
      await setMeta('vault_key', null, db)
      await setMeta('vault_key_id', null, db)
      await setMeta('vault_key_prev', null, db)
      await setMeta('vault_key_prev_id', null, db)
    }
    await setMeta('roster_version', null, db)
    await setMeta('roster_downloaded_at', null, db)
  })
  resetVaultKeyCache()
}

export type RosterFreshness = 'missing' | 'fresh' | 'stale' | 'expired'

/** fresh < 24 h ≤ stale < maxAgeDays ≤ expired (F4.1 warns after a day; N-F4-04 blocks after N days). */
export function rosterFreshness(
  downloadedAt: number | null | undefined,
  maxAgeDays: number,
  now = Date.now(),
): RosterFreshness {
  if (!downloadedAt) return 'missing'
  const age = now - downloadedAt
  if (age >= maxAgeDays * 86_400_000) return 'expired'
  if (age >= 86_400_000) return 'stale'
  return 'fresh'
}
